/** A recorded segment as epoch milliseconds, sorted by start. */
export interface Seg {
  id: string;
  start: number;
  end: number;
}

export interface Span {
  start: number;
  end: number;
}

/** Joints between consecutive segments up to this long are not gaps. */
export const joinMs = 3000;

export function toSegs(items: { id: string; started_at: string; ended_at: string }[]): Seg[] {
  return items
    .map((s) => ({ id: s.id, start: Date.parse(s.started_at), end: Date.parse(s.ended_at) }))
    .filter((s) => Number.isFinite(s.start) && s.end >= s.start)
    .sort((a, b) => a.start - b.start);
}

/** Recorded spans: segments merged across short joints. */
export function coverage(segs: Seg[]): Span[] {
  const out: Span[] = [];
  for (const s of segs) {
    const last = out.at(-1);
    if (last && s.start - last.end <= joinMs) last.end = Math.max(last.end, s.end);
    else out.push({ start: s.start, end: s.end });
  }
  return out;
}

/**
 * The segment shown at instant t: the one containing it, or the next one when
 * t falls in a short joint (drawn as recorded). Null in a real gap.
 */
export function segmentNear(segs: Seg[], t: number): Seg | null {
  for (const s of segs) {
    if (t < s.start) return s.start - t <= joinMs ? s : null;
    if (t < s.end) return s;
  }
  return null;
}

export function recordedMs(spans: Span[]): number {
  return spans.reduce((a, s) => a + (s.end - s.start), 0);
}

/**
 * Where to play instant t: the segment containing it, or else the next
 * recorded one (jumping a gap). Null after the last recording.
 */
export function locate(segs: Seg[], t: number): { seg: Seg; offsetMs: number } | null {
  for (const s of segs) {
    if (t < s.start) return { seg: s, offsetMs: 0 };
    if (t < s.end) return { seg: s, offsetMs: t - s.start };
  }
  return null;
}

export function nextAfter(segs: Seg[], id: string): Seg | null {
  const i = segs.findIndex((s) => s.id === id);
  return i >= 0 ? (segs[i + 1] ?? null) : null;
}

/** Start of the previous/next recorded span relative to t, for gap jumps. */
export function spanStartBefore(spans: Span[], t: number): number | null {
  let found: number | null = null;
  for (const s of spans) {
    if (s.start < t - 1000) found = s.start;
  }
  return found;
}

export function spanStartAfter(spans: Span[], t: number): number | null {
  return spans.find((s) => s.start > t + 1000)?.start ?? null;
}
